// The Munos type system as represented in the compiler.
//
// `Type` is the resolved internal form, distinct from the AST's `TypeExpr`
// (which is the syntactic form a programmer writes). The type-checker
// produces `Type` values from `TypeExpr`s and from expression inference.
// Singleton primitive types — fine to share since they're structurally
// immutable.
export const T_I8 = { kind: "i8" };
export const T_I16 = { kind: "i16" };
export const T_I32 = { kind: "i32" };
export const T_U8 = { kind: "u8" };
export const T_U16 = { kind: "u16" };
export const T_U32 = { kind: "u32" };
export const T_BOOL = { kind: "bool" };
export const T_STRING = { kind: "string" };
export const T_IMAGE = { kind: "image" };
export const T_SOUND = { kind: "sound" };
export const T_CLIENT = { kind: "client" };
export const T_VOID = { kind: "void" };
export const T_ERROR = { kind: "error" };
export function isIntType(t) {
    switch (t.kind) {
        case "i8":
        case "i16":
        case "i32":
        case "u8":
        case "u16":
        case "u32":
            return true;
        default:
            return false;
    }
}
export function isSignedInt(t) {
    return t.kind === "i8" || t.kind === "i16" || t.kind === "i32";
}
// A heap type the escaped-generation refcount machinery (promoteEscaped /
// retain / release) can manage: a value laid out as the 16-byte array/string
// header + a flat data block of scalars or string pointers. That is a `string`,
// or an array whose elements are scalars (u8[], i32[], …) or strings
// (string[]). Excluded: structs (inline bytes, no header), image/sound (host
// descriptors), and arrays of composites (no such nesting exists — the checker
// bans it — but the guard keeps the barrier honest). See docs/memory-model.md
// §"Stage 2".
export function isRefcountableType(t) {
    if (!t)
        return false;
    if (t.kind === "string")
        return true;
    if (t.kind === "array") {
        const e = t.element;
        return isIntType(e) || e.kind === "bool" || e.kind === "client" || e.kind === "string";
    }
    return false;
}
// A `string<N>[M]` — an array whose element is a BOUNDED string. Its elements
// are stored inline as fixed-size cells (a flat blob), not as pointers to
// separate string objects (docs/memory-model.md §"Stage 2"). Consequences the
// analysis and codegen key off: an element store (`g[i] = s`) copies the source
// bytes INTO the array's own data block — a value copy, so nothing escapes and
// no per-element refcount is needed — and an element read copies a cell OUT to a
// fresh standalone string. The whole array is promoted/reclaimed as one unit.
// An UNBOUNDED `string[]` (no `<N>`) stays an array of refcounted pointers.
export function isInlineStringArray(t) {
    return t?.kind === "array" && t.element.kind === "string" && t.element.capacity !== undefined;
}
// The inclusive value range of an integer type, as bigints. Null for a
// non-integer type.
export function intRange(t) {
    switch (t.kind) {
        case "i8": return { min: -(1n << 7n), max: (1n << 7n) - 1n };
        case "i16": return { min: -(1n << 15n), max: (1n << 15n) - 1n };
        case "i32": return { min: -(1n << 31n), max: (1n << 31n) - 1n };
        case "u8": return { min: 0n, max: (1n << 8n) - 1n };
        case "u16": return { min: 0n, max: (1n << 16n) - 1n };
        case "u32": return { min: 0n, max: (1n << 32n) - 1n };
        default: return null;
    }
}
// True if every value of `from` is representable in `to` — a lossless integer
// widening. Covers `i8→i16→i32`, `u8→u16→u32`, and unsigned→wider-signed
// (`u8→i16/i32`, `u16→i32`). Excludes narrowing, signed→unsigned (negatives
// don't fit), and same-width sign crossings (`u8`↔`i8`); those still need an
// explicit cast. Because every sized integer is stored in an i32 with its
// value already sign-/zero-extended to 32 bits, a widening is a no-op at the
// bit level — no codegen conversion is emitted.
export function canWiden(from, to) {
    if (!isIntType(from) || !isIntType(to))
        return false;
    if (typeEquals(from, to))
        return false;
    const f = intRange(from);
    const t = intRange(to);
    if (!f || !t)
        return false;
    return f.min >= t.min && f.max <= t.max;
}
export function intBits(t) {
    switch (t.kind) {
        case "i8":
        case "u8": return 8;
        case "i16":
        case "u16": return 16;
        case "i32":
        case "u32": return 32;
        default: return null;
    }
}
export function typeEquals(a, b) {
    if (a.kind !== b.kind)
        return false;
    if (a.kind === "array" && b.kind === "array") {
        // Array *size* is not part of type identity: every `u8[N]` is the same
        // type as `u8[]`. Element-type equality is what counts — and for a
        // `string<N>` element that now includes the capacity (below), because a
        // bounded-string array's inline-cell stride is a function of N: a
        // `string<8>[]` and a `string<4>[]` have different in-memory layouts and
        // are NOT interchangeable (see arrayElementStride, docs/memory-model.md
        // slice 4/6).
        return typeEquals(a.element, b.element);
    }
    if (a.kind === "struct" && b.kind === "struct") {
        return a.name === b.name;
    }
    if (a.kind === "string" && b.kind === "string") {
        // Capacity IS part of a string's identity: `string<4>` and `string<8>`
        // are distinct types (two unbounded strings compare equal). This makes
        // element-wise array equality reject a cell-stride mismatch. Assignment's
        // looser widening relation (`sourceCap <= targetCap`, and byte-wise `==`)
        // is handled separately — see isAssignable and checkBinary.
        return a.capacity === b.capacity;
    }
    return true;
}
export function typeToString(t) {
    switch (t.kind) {
        case "array": {
            const inner = typeToString(t.element);
            return t.size !== undefined ? `${inner}[${t.size}]` : `${inner}[]`;
        }
        case "struct":
            return t.name;
        case "string":
            return t.capacity !== undefined ? `string<${t.capacity}>` : "string";
        default:
            return t.kind;
    }
}
// Returns true if `value` fits into the integer type `t`.
export function intLiteralFits(value, t) {
    switch (t.kind) {
        case "i8": return value >= -(1n << 7n) && value < (1n << 7n);
        case "i16": return value >= -(1n << 15n) && value < (1n << 15n);
        case "i32": return value >= -(1n << 31n) && value < (1n << 31n);
        case "u8": return value >= 0n && value < (1n << 8n);
        case "u16": return value >= 0n && value < (1n << 16n);
        case "u32": return value >= 0n && value < (1n << 32n);
        default: return false;
    }
}
//# sourceMappingURL=types.js.map